import React, { type FC } from 'react';
import { Col, Row } from 'antd';
import { parse } from 'date-fns';
import { DatePicker, InputText, Radio, Select, Space, Textarea } from '@g2u/ui-kit';

import { Condition, TechnicianType, type Technician, type WithOptionalDataTest } from 'types';
import { Section } from 'components';
import styles from './administration-fields.module.scss';

export namespace TechnicianAdministrationFields {
  export type Props = {
    title?: string;
    inline?: boolean;
    technician: Technician | null;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const { inline, title, technician } = props;

    const formatRate = (value: string | number | undefined) => {
      if (value === undefined || value === null) return '';
      const cleaned = String(value).replace(/[^0-9.]/g, '');
      const [intPart, decimalPart] = cleaned.split('.');
      return decimalPart !== undefined ? `${intPart}.${decimalPart.slice(0, 2)}` : intPart;
    };

    return (
      <Section.$ title={title ?? 'Administration'} inline={inline} className={styles.administrationFields}>
        <Row gutter={[32, 8]}>
          <Col xl={{ span: 12 }}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
              <Radio.$
                required
                initialValue={technician?.book_status?.toString()}
                label="Booking Status: "
                name="book_status"
                options={[
                  {
                    label: <>Available</>,
                    value: '1',
                  },
                  {
                    label: <>Unavailable</>,
                    value: '0',
                  },
                ]}
                rules={[{ required: true }]}
              />
              <Radio.$
                required
                initialValue={technician?.default_availability}
                label="Availability Default: "
                name="default_availability"
                options={[
                  {
                    label: <>Available</>,
                    value: Condition.Yes,
                  },
                  {
                    label: <>Requires Confirmation</>,
                    value: Condition.No,
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Radio.$
                required
                initialValue={technician?.sds_availability}
                label="Same Day Service: "
                name="sds_availability"
                options={[
                  {
                    label: <>Available</>,
                    value: Condition.Yes,
                  },
                  {
                    label: <>Requires Confirmation</>,
                    value: Condition.No,
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Radio.$
                required
                initialValue={technician?.travel_buffer?.toString()}
                label="Travel Buffer: "
                name="travel_buffer"
                options={[
                  {
                    label: <>0.5 Hr</>,
                    value: '0.50',
                  },
                  {
                    label: <>1.0 Hr</>,
                    value: '1.00',
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Radio.$
                required
                initialValue={technician?.smart_travel_buffer?.toString()}
                label="Smart Travel Buffer: "
                name="smart_travel_buffer"
                options={[
                  {
                    label: <>Yes</>,
                    value: '1',
                  },
                  {
                    label: <>No</>,
                    value: '0',
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Radio.$
                required
                initialValue={technician?.surrounding_areas?.toString()}
                label="Surrounding Areas: "
                name="surrounding_areas"
                options={[
                  {
                    label: <>Yes</>,
                    value: '1',
                  },
                  {
                    label: <>No</>,
                    value: '0',
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Textarea.$ name="notes" placeholder="" label="Note:" initialValue={technician?.notes} />
            </Space.$>
          </Col>

          <Col xl={{ span: 12 }}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
              <DatePicker.$
                allowClear={false}
                name="date_started"
                label="Date Started: "
                initialValue={
                  technician?.date_started && technician.date_started !== '0000-00-00'
                    ? (parse(technician.date_started, 'yyyy-MM-dd', new Date()) as any)
                    : undefined
                }
                format="YYYY-MM-DD"
              />
              <DatePicker.$
                name="date_finished"
                label="Date Finished: "
                initialValue={
                  technician?.date_finished && technician.date_finished !== '0000-00-00'
                    ? (parse(technician.date_finished, 'yyyy-MM-dd', new Date()) as any)
                    : undefined
                }
                format="YYYY-MM-DD"
              />

              <Radio.$
                required
                initialValue={technician?.active}
                label="Active: "
                name="active"
                options={[
                  {
                    label: <>Yes</>,
                    value: Condition.Yes,
                  },
                  {
                    label: <>No</>,
                    value: Condition.No,
                  },
                ]}
                rules={[{ required: true }]}
              />

              <Select.$
                required
                name="tech_type"
                label="Technician Type: "
                placeholder={'Select type'}
                initialValue={technician?.tech_type}
                rules={[{ required: true }]}
                options={[
                  {
                    label: 'Technician',
                    value: TechnicianType.Technician,
                  },
                  {
                    label: 'Consultant',
                    value: TechnicianType.Consultant,
                  },
                  {
                    label: 'Employee',
                    value: TechnicianType.Employee,
                  },
                  {
                    label: 'Franchisee',
                    value: TechnicianType.Franchisee,
                  },
                  {
                    label: 'FT Tech',
                    value: TechnicianType.FTTech,
                  },
                ]}
              />

              <Select.$
                required
                name="category"
                label="Category: "
                placeholder={'Select category'}
                initialValue={technician?.category}
                rules={[{ required: true }]}
                options={[
                  {
                    label: 'Home',
                    value: 'H',
                  },
                  {
                    label: 'Business',
                    value: 'B',
                  },
                  {
                    label: 'Both',
                    value: 'A',
                  },
                ]}
              />

              <InputText.$
                label="Labor Day Home:"
                name="labour_fee_soho"
                placeholder=""
                initialValue={technician?.labour_fee_soho}
                prefix="$"
                formatter={formatRate}
              />
              <InputText.$
                label="Labour Fee Business:"
                name="labour_fee_business"
                formatter={formatRate}
                placeholder=""
                initialValue={technician?.labour_fee_business}
                prefix="$"
              />
              <InputText.$
                label="Parts Rate Percentage:"
                name="parts_rate_threshold"
                placeholder=""
                initialValue={technician?.parts_rate_threshold}
              />

              <Select.$
                required
                name="priority"
                label="Priority: "
                placeholder={'Select priority'}
                initialValue={technician?.priority}
                rules={[{ required: true }]}
                options={[
                  {
                    label: '1',
                    value: '1',
                  },
                  {
                    label: '2',
                    value: '2',
                  },
                  {
                    label: '3',
                    value: '3',
                  },
                  {
                    label: '4',
                    value: '4',
                  },
                  {
                    label: '5',
                    value: '5',
                  },
                ]}
              />
            </Space.$>
          </Col>
        </Row>
      </Section.$>
    );
  };
}
